import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { getLanguages, getSubjects } from '@app/store/reducers/data.reducer';

import { environment } from '@env/environment';
import { Store } from '@ngrx/store';
import { BffGetListMeetingsEndpoint, BffGetOneMeetingEndpoint, type Pagination } from 'lingo2-api-models';
import type { Language, Meeting, Subject } from 'lingo2-models';
import { map, type Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class MeetingsBffService {
  private readonly http = inject(HttpClient);
  private readonly store = inject(Store);

  private subjects: Record<number, Subject> = {};
  private languages: Record<number, Language> = {};
  private baseUrl = (path: string): string => `${environment.bff_url}/${path}`;

  constructor() {
    this.store.select(getSubjects).subscribe((subjects) => {
      this.subjects = subjects.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    });
    this.store.select(getLanguages).subscribe((languages) => {
      this.languages = languages.all.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {});
    });
  }

  getOne(dto: BffGetOneMeetingEndpoint.Body): Observable<Meeting | null> {
    return this.http
      .post<BffGetOneMeetingEndpoint.Response>(this.baseUrl(BffGetOneMeetingEndpoint.path), dto, {
        observe: 'body',
      })
      .pipe(
        map((response) => {
          if (response.status === 'ok') {
            // TODO: Remove 'as unknown as Meeting' when the API response is fixed
            const meeting = response.data.item as unknown as Meeting;
            this.mapDictionary(meeting);

            return meeting;
          } else {
            console.error(response.errorMessage);
            return null;
          }
        }),
      );
  }

  getList(dto: BffGetListMeetingsEndpoint.Body): Observable<{ list: Meeting[]; pagination: Pagination }> {
    return this.http
      .post<BffGetListMeetingsEndpoint.Response>(this.baseUrl(BffGetListMeetingsEndpoint.path), dto, {
        observe: 'body',
      })
      .pipe(
        map((response) => {
          if (response.status === 'ok') {
            // TODO: Remove 'as unknown as { list: Meeting[]; pagination: Pagination }' when the API response is fixed
            const data = response.data as unknown as { list: Meeting[]; pagination: Pagination };
            data.list.forEach((meeting) => this.mapDictionary(meeting));
            return data;
          } else {
            console.error(response.errorMessage);
            return { list: [], pagination: null };
          }
        }),
      );
  }

  private mapDictionary(meeting: Meeting) {
    if (meeting.subject_id) meeting.subject = this.subjects[meeting.subject_id];
    if (meeting.language_id) meeting.language = this.languages[meeting.language_id];
    if (meeting.user_service) {
      meeting.user_service.subject = this.subjects[meeting.subject_id];
      meeting.user_service.language = this.languages[meeting.language_id];
    }
  }
}
